import React from "react";
import { Accordion, Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import KpiFieldRenderer from "@features/report/activity-entry/components/KpiFieldRenderer";
import {
    ActivityContext,
    getActivityKpiContexts,
    getActivityAccordionKey,
    getKpiValueKey,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import { isRequiredForPeriod } from "@core/reports/utils/requiredStarting";
import {
    resolveKpiGrid,
    resolveKpiOperands,
    resolveKpiOptions,
} from "@features/report/activity-entry/utils/kpiFieldConfig";
import { isCalculatedEntryMode, isGridDataType } from "@core/reports/utils/fieldTypeUtils";
import { formatDateOnly } from "@core/utils/formatDateOnly";

type SubmittableKpi = {
    kpi_is_required?: boolean;
    kpi_required_at?: string | null;
    submission?: {
        activity_entry_status_name?: string | null;
        activity_entry_status_id?: number | null;
    } | null;
};

export interface ActivityFormAccordionProps {
    contexts: ActivityContext[];
    openKeys: string[];
    onToggleKey: (key: string) => void;
    showInactive: boolean;
    values: Record<string, string>;
    activityEditable: boolean;
    showSaveButton?: boolean;
    busy: boolean;
    tagIsClosed: boolean;
    reportTagSince?: string | null;
    reportTagUntil?: string | null;
    isActivityDirty: (context: ActivityContext) => boolean;
    areAllKpisSubmittedForActivity: (context: ActivityContext) => boolean;
    isKpiSubmitted: (kpi: SubmittableKpi) => boolean;
    onChange: (subGroupId: number | null, activityId: number, kpiId: number, value: string) => void;
    onSaveActivity: (
        context: ActivityContext,
        override?: { kpiId: number; kpiValue: string }
    ) => void | Promise<unknown>;
    /** Called when a KPI grid maximize modal opens or closes. */
    onGridModalOpenChange?: (open: boolean) => void;
    /** Map of kpi_id → calculated KPI warning message. */
    calculatedWarningsByKpiId?: Record<number, string>;
    /**
     * Template preview: force required star from kpi_is_required, show Required at,
     * and pass previewMode to field renderer.
     */
    previewMode?: boolean;
}

/**
 * Presentational accordion of activities → KPI inputs for the activity entry
 * form. Extracted verbatim from ActivityEntryForm; all state/predicates/handlers
 * are supplied as props so this component holds no state of its own.
 */
export default function ActivityFormAccordion({
    contexts,
    openKeys,
    onToggleKey,
    showInactive,
    values,
    activityEditable,
    showSaveButton = false,
    busy,
    tagIsClosed,
    reportTagSince = null,
    reportTagUntil = null,
    isActivityDirty,
    areAllKpisSubmittedForActivity,
    isKpiSubmitted,
    onChange,
    onSaveActivity,
    onGridModalOpenChange,
    calculatedWarningsByKpiId = {},
    previewMode = false,
}: ActivityFormAccordionProps) {
    if (contexts.length === 0) {
        return (
            <div className="p-3 p-md-4 text-muted small">
                No activities assigned.
            </div>
        );
    }

    return (
        <Accordion activeKey={openKeys} alwaysOpen className="activity-entry-accordion">
            {contexts.map((context) => {
                const act = context.activity;
                const accordionKey = getActivityAccordionKey(
                    context.subGroupId,
                    act.activity_id
                );
                const kpiContexts = getActivityKpiContexts(context, showInactive);
                const activityAllSubmitted = areAllKpisSubmittedForActivity(context);

                return (
                    <Accordion.Item eventKey={accordionKey} key={accordionKey}>
                        <Accordion.Header onClick={() => onToggleKey(accordionKey)}>
                            <div className="w-100 d-flex align-items-center gap-2">
                                <div className="flex-grow-1 text-break">
                                    {act.activity_name}
                                </div>

                                {isActivityDirty(context) ? (
                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                        <Badge {...subtleBadgeProps("warning")} pill>Unsaved</Badge>
                                    </div>
                                ) : null}
                            </div>
                        </Accordion.Header>

                        <Accordion.Body className="p-0">
                            <div className="p-3 p-md-4">
                                {kpiContexts.length === 0 ? (
                                    <div className="text-muted small">
                                        No KPIs assigned to this activity.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column">
                                        {kpiContexts.map(({ kpi }, index) => {
                                            const key = getKpiValueKey(
                                                context.subGroupId,
                                                act.activity_id,
                                                kpi.kpi_id
                                            );
                                            const dataTypeName =
                                                kpi.data_type?.data_type_name ?? "";
                                            const unitLabel =
                                                kpi.unit?.unit_abbreviation ||
                                                kpi.unit?.unit_name ||
                                                null;
                                            const unitPosition =
                                                kpi.unit?.unit_display_position === "prefix"
                                                    ? "prefix"
                                                    : "suffix";
                                            const calculated = isCalculatedEntryMode(kpi.entry_mode);
                                            const inputDisabled =
                                                !activityEditable ||
                                                busy ||
                                                isKpiSubmitted(kpi) ||
                                                calculated;
                                            const inputId = `activity-kpi-${context.group.report_template.report_template_id}-${context.subGroupId ?? "base"}-${act.activity_id}-${kpi.kpi_id}`;
                                            const labelId = `${inputId}-label`;
                                            const kpiRequiredForTag = previewMode
                                                ? Boolean(kpi.kpi_is_required)
                                                : isRequiredForPeriod(
                                                      !!kpi.kpi_is_required,
                                                      kpi.kpi_required_at,
                                                      reportTagSince,
                                                      reportTagUntil
                                                  );
                                            const { operation, operands } = resolveKpiOperands(kpi);
                                            const options = resolveKpiOptions(kpi);
                                            const grid = resolveKpiGrid(kpi);
                                            const useGrid = isGridDataType(dataTypeName);
                                            const labelColProps = useGrid
                                                ? { xs: 12 as const }
                                                : { xs: 12 as const, md: 5 as const };
                                            const fieldColProps = useGrid
                                                ? { xs: 12 as const }
                                                : { xs: 12 as const, md: 7 as const };

                                            return (
                                                <div
                                                    key={`${context.subGroupId ?? "base"}-${kpi.kpi_id}`}
                                                    className={index > 0 ? "pt-3 mt-3" : ""}
                                                >
                                                    <Row className="g-2 align-items-start">
                                                        <Col {...labelColProps}>
                                                            <div className="d-flex flex-column gap-1">
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <div
                                                                        className="d-flex align-items-center gap-1 flex-wrap"
                                                                        style={{ fontWeight: 400 }}
                                                                    >
                                                                        <span id={labelId}>
                                                                            {kpi.kpi_name}
                                                                        </span>

                                                                        {kpiRequiredForTag && (
                                                                            <span
                                                                                className="text-danger"
                                                                                title="This KPI is required"
                                                                                aria-label="Required KPI"
                                                                            >
                                                                                *
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {previewMode && kpi.kpi_is_required ? (
                                                                    <div className="text-muted small">
                                                                        Required at:{" "}
                                                                        <span className="fw-semibold">
                                                                            {formatDateOnly(kpi.kpi_required_at) ??
                                                                                "—"}
                                                                        </span>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </Col>

                                                        <Col {...fieldColProps}>
                                                            <Form.Group className="mb-0">
                                                                <KpiFieldRenderer
                                                                    inputId={inputId}
                                                                    labelId={labelId}
                                                                    kpiName={kpi.kpi_name}
                                                                    dataTypeName={dataTypeName}
                                                                    unitLabel={unitLabel}
                                                                    unitPosition={unitPosition}
                                                                    value={values[key] ?? ""}
                                                                    required={kpiRequiredForTag}
                                                                    disabled={inputDisabled || previewMode}
                                                                    previewMode={previewMode}
                                                                    entryMode={kpi.entry_mode}
                                                                    operation={operation}
                                                                    operands={operands}
                                                                    options={options}
                                                                    predefinedOptionListCode={
                                                                        kpi.predefined_option_list_code ?? null
                                                                    }
                                                                    optionListError={kpi.option_list_error ?? null}
                                                                    gridColumns={grid.columns}
                                                                    gridFixedRows={grid.fixedRows}
                                                                    gridMinRows={grid.minRows}
                                                                    gridMaxRows={grid.maxRows}
                                                                    gridShowColumnTotals={grid.showColumnTotals}
                                                                    gridShowRowTotals={grid.showRowTotals}
                                                                    warningMessage={
                                                                        calculatedWarningsByKpiId[kpi.kpi_id] ??
                                                                        null
                                                                    }
                                                                    onChange={(v) =>
                                                                        onChange(
                                                                            context.subGroupId,
                                                                            act.activity_id,
                                                                            kpi.kpi_id,
                                                                            v
                                                                        )
                                                                    }
                                                                    onGridModalOpenChange={
                                                                        onGridModalOpenChange
                                                                    }
                                                                    onSaveDraft={
                                                                        showSaveButton && !previewMode
                                                                            ? async (serialized) =>
                                                                                  onSaveActivity(context, {
                                                                                      kpiId: kpi.kpi_id,
                                                                                      kpiValue: serialized,
                                                                                  })
                                                                            : undefined
                                                                    }
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {showSaveButton ? (
                                <div className="activity-entry-accordion__footer px-3 px-md-4 py-2 d-flex justify-content-end">
                                    <Button
                                        variant="outline-secondary"
                                        className="btn-action"
                                        onClick={() => onSaveActivity(context)}
                                        disabled={busy || activityAllSubmitted || tagIsClosed}
                                        aria-busy={busy}
                                    >
                                        {busy ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                    aria-hidden
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save Draft"
                                        )}
                                    </Button>
                                </div>
                            ) : null}
                        </Accordion.Body>
                    </Accordion.Item>
                );
            })}
        </Accordion>
    );
}
